import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import PageHeader from './ui/PageHeader';
import CustomDropdown from './ui/CustomDropdown';
import ComentariosSection from './ComentariosSection';
import { useSidebar } from '../contexts/SidebarContext';
import { useAdmin } from '../contexts/AdminContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { useTranslation } from 'react-i18next';
import { typography, borderRadius } from '../styles/typography';
import axios from 'axios';
import { toast } from 'sonner';
import {
  GraduationCap, Play, BookOpen, Download, Heart, Eye, Search,
  Plus, X, CheckCircle, FileText, Edit3, Trash2,
  Archive, Code, ArrowLeft, ExternalLink,
  Upload, Image, ChevronDown, Loader2, ArrowRight,
  Home
} from 'lucide-react';
import AcademiaHome from './academia/AcademiaHome';
import CourseView from './academia/CourseView';
import MediaCard from './academia/MediaCard';
import { isNovidade } from './academia/academiaUtils';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ===== HELPERS =====

const extractYoutubeId = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

const getThumbnail = (url) => {
  const videoId = extractYoutubeId(url);
  return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;
};

const NIVEL_COLORS = {
  iniciante: '#34D399',
  intermediario: '#F59E0B',
  avancado: '#EF4444',
};

const FILE_TYPE_CONFIG = {
  pdf: { color: '#EF4444', Icon: FileText },
  md: { color: '#3B82F6', Icon: FileText },
  txt: { color: 'var(--dp-text-tertiary)', Icon: FileText },
  zip: { color: '#F59E0B', Icon: Archive },
  json: { color: '#34D399', Icon: Code },
  docx: { color: '#3B82F6', Icon: FileText },
  xlsx: { color: '#10B981', Icon: FileText },
  pptx: { color: '#FB923C', Icon: FileText },
  csv: { color: '#34D399', Icon: FileText },
};

const INITIAL_FORM = {
  tipo: 'treinamento',
  titulo: '',
  descricao: '',
  conteudo_rich: '',
  categoria: 'Geral',
  subcategoria: '',
  tags: [],
  nivel: '',
  thumbnail_url: '',
  video_url: '',
  download_url: '',
  tipo_arquivo: '',
  tamanho_arquivo: '',
  resource_type: 'file', // 'file' (upload R2) ou 'link' (URL externa)
  curso_pai_id: '',
  ordem: 0,
  total_aulas: 0,
  duracao_estimada: '',
  publicado: true,
  destaque: false,
};

// Extensões que abrem em nova aba (navegador renderiza nativamente)
const OPEN_IN_TAB_EXTENSIONS = ['pdf', 'txt'];

/**
 * Força download de arquivo via fetch + blob.
 * Mesma lógica usada no Gerador de Áudio (downloadAudio).
 * Para PDF/TXT, o navegador renderiza inline — usamos window.open em vez disso.
 */
const forceDownloadFile = async (url, filename) => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error('Erro no download');
    const blob = await response.blob();
    if (!blob || blob.size === 0) throw new Error('Arquivo vazio');
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    return true;
  } catch (e) {
    // Fallback: tenta anchor direto
    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return true;
    } catch {
      window.open(url, '_blank');
      return false;
    }
  }
};

/**
 * Detecta ícone/label para links externos baseado no domínio.
 */
const getExternalLinkInfo = (url) => {
  if (!url) return { label: 'Abrir link', icon: 'link' };
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    if (hostname.includes('drive.google')) return { label: 'Google Drive', icon: 'drive' };
    if (hostname.includes('dropbox')) return { label: 'Dropbox', icon: 'dropbox' };
    if (hostname.includes('onedrive') || hostname.includes('sharepoint')) return { label: 'OneDrive', icon: 'onedrive' };
    if (hostname.includes('mega.nz')) return { label: 'MEGA', icon: 'mega' };
    if (hostname.includes('github')) return { label: 'GitHub', icon: 'github' };
    return { label: 'Abrir link', icon: 'link' };
  } catch {
    return { label: 'Abrir link', icon: 'link' };
  }
};

// ===== MAIN COMPONENT =====

const AcademiaPage = ({ onLogout }) => {
  const { t } = useTranslation();
  usePageTitle(t('academia.title'));
  const { sidebarWidth, isMobile } = useSidebar();
  const { isSuperAdmin } = useAdmin();

  // Tab state — sync with URL query param ?tab=
  // 'inicio' é a aba padrão (central de aprendizado); os deep-links do
  // sidebar (?tab=treinamento|cursos|recursos) continuam funcionando.
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const validTabs = ['inicio', 'treinamento', 'cursos', 'recursos'];
  const [activeTab, setActiveTab] = useState(
    validTabs.includes(tabFromUrl) ? tabFromUrl : 'inicio'
  );

  // Sync tab when URL query changes
  useEffect(() => {
    if (tabFromUrl && validTabs.includes(tabFromUrl) && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
      setSelectedItem(null);
    }
  }, [tabFromUrl]);

  // Data state
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Detail / Split view
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Search & Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Admin modal
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ ...INITIAL_FORM });
  const [tagInput, setTagInput] = useState('');
  const [courses, setCourses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showMoreDetails, setShowMoreDetails] = useState(false);

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadPreview, setUploadPreview] = useState(null); // { url, type, name }
  const [resourceThumbPreview, setResourceThumbPreview] = useState(null); // { url, name }
  const coverInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const resourceThumbInputRef = useRef(null);

  const searchTimeoutRef = useRef(null);

  // Catálogo agregado (alimenta hero + fileiras de TODAS as seções)
  const [inicioData, setInicioData] = useState({ cursos: [], treinamentos: [], recursos: [] });
  const [inicioLoading, setInicioLoading] = useState(true);

  // Progresso geral do usuário (continue de onde parou + barras nos cards)
  const [progressoGeral, setProgressoGeral] = useState(null);

  // Ordem das seções do Início (configurável pelo super admin)
  const [layoutSecoes, setLayoutSecoes] = useState([]);
  // Ordem das SUBCATEGORIAS por categoria: {"<categoria>": [nomes em ordem]}
  const [layoutSubs, setLayoutSubs] = useState({});

  // Filtro de categoria dos cards do topo (id da fileira, ex.: 'cat-App').
  // Vive AQUI (não no AcademiaHome) de propósito: abrir um vídeo desmonta o
  // Home, e ao VOLTAR o usuário cai na mesma seção filtrada — só sai quando
  // desmarcar o card ou trocar de aba.
  const [categoryFilter, setCategoryFilter] = useState(null);

  // ===== API CALLS =====

  const tipoMapping = {
    treinamento: 'treinamento',
    cursos: 'curso',
    recursos: 'recurso',
  };

  const loadItems = useCallback(async () => {
    // Layout Netflix: as fileiras vêm de loadInicio. Este fetch atende SÓ a
    // busca global cross-tab; sem busca não há lista a carregar (e o loading
    // PRECISA resolver — auditoria: skeleton infinito ao abrir item do Home).
    if (!debouncedSearch) { setLoading(false); return; }
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('search', debouncedSearch);
      params.append('sort', 'ordem');

      const res = await axios.get(`${API}/academia/items?${params.toString()}`);
      setItems(res.data.items || []);
    } catch (err) {
      console.error('Error loading academia items:', err);
      if (err.response?.status !== 401) {
        toast.error(t('academia.empty.busca'));
      }
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, t]);

  // Load items when filters change
  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Progresso do usuário (GET /academia/progresso — contrato existente)
  const loadProgresso = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/academia/progresso`);
      setProgressoGeral(res.data || null);
    } catch (err) {
      // Progresso é enriquecimento: falha não bloqueia a página
      console.error('Error loading progresso:', err);
    }
  }, []);

  // Dados agregados da aba Início (cursos + treinamentos + nº de recursos)
  const loadInicio = useCallback(async () => {
    try {
      setInicioLoading(true);
      const [cursosRes, treinosRes, recursosRes] = await Promise.all([
        axios.get(`${API}/academia/items?tipo=curso&sort=ordem`),
        axios.get(`${API}/academia/items?tipo=treinamento&sort=ordem`),
        axios.get(`${API}/academia/items?tipo=recurso`),
      ]);
      setInicioData({
        cursos: cursosRes.data.items || [],
        treinamentos: treinosRes.data.items || [],
        recursos: recursosRes.data.items || [],
      });
    } catch (err) {
      console.error('Error loading academia home:', err);
    } finally {
      setInicioLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProgresso();
  }, [loadProgresso]);

  // Layout (ordem das seções) — GET para todos; PUT só super admin
  const loadLayout = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/academia/layout`);
      setLayoutSecoes(res.data?.secoes || []);
      setLayoutSubs(res.data?.subs || {});
    } catch (err) {
      console.error('Error loading layout:', err);
    }
  }, []);

  useEffect(() => { loadLayout(); }, [loadLayout]);

  const handleReorderSections = async (secoes) => {
    setLayoutSecoes(secoes); // otimista
    try {
      await axios.put(`${API}/academia/layout`, { secoes });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar ordem das seções');
      loadLayout();
    }
  };

  // Reordena SUBCATEGORIAS de uma categoria (setas ↑↓ no título da subfileira)
  const handleReorderSubs = async (categoria, nomes) => {
    const novo = { ...layoutSubs, [categoria]: nomes };
    setLayoutSubs(novo); // otimista
    try {
      await axios.put(`${API}/academia/layout`, { secoes: layoutSecoes, subs: novo });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar ordem das subcategorias');
      loadLayout();
    }
  };

  // Reordena cards: PUT ordem item a item e recarrega o catálogo
  const handleReorderItems = async (updates) => {
    try {
      for (const u of updates) {
        await axios.put(`${API}/academia/items/${u.id}`, { ordem: u.ordem });
      }
      await loadInicio();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar ordem dos cards');
    }
  };

  // Catálogo carrega no mount (todas as seções usam) e é re-buscado após CRUD
  useEffect(() => {
    loadInicio();
  }, [loadInicio]);

  // Mapa cursoId -> percentual (barras de progresso nos cards de curso)
  const progressoMap = React.useMemo(() => {
    const map = {};
    (progressoGeral?.cursos_em_andamento || []).forEach(c => { map[c.id] = c.percentual; });
    return map;
  }, [progressoGeral]);

  // Debounced search — real 300ms debounce
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [searchTerm]);

  // Trocar de seção fecha o detalhe aberto e limpa o filtro de categoria
  // (busca cross-tab é mantida)
  useEffect(() => {
    setSelectedItem(null);
    setCategoryFilter(null);
  }, [activeTab]);

  // Load courses for admin modal dropdown
  const loadCourses = async () => {
    try {
      const res = await axios.get(`${API}/academia/items?tipo=curso`);
      setCourses(res.data.items || []);
    } catch (err) {
      console.error('Error loading courses:', err);
    }
  };

  // Open detail (curso → CourseView; demais → player)
  // opts.resume: abre DIRETO a próxima aula não assistida (1 clique, padrão
  // Netflix/Circle — achado da auditoria).
  const openDetail = async (item, opts = {}) => {
    if (detailLoading) return; // evita duplo clique durante o fetch
    // Cursos de EXEMPLO (preview do design com catálogo vazio) não abrem player
    if (item?.__mock) {
      toast(t('academia.home.mock_notice') || 'Conteúdo de exemplo. Adicione cursos reais para ativar a trilha.');
      return;
    }
    try {
      setDetailLoading(true);
      const res = await axios.get(`${API}/academia/items/${item.id}`);
      if (opts.resume && res.data?.tipo === 'curso' && Array.isArray(res.data.aulas) && res.data.aulas.length > 0) {
        const next = res.data.aulas.find(a => !a.user_watched) || res.data.aulas[0];
        const aulaRes = await axios.get(`${API}/academia/items/${next.id}`);
        // _cursoPai: o "voltar" do player retorna ao curso, não à grade
        setSelectedItem({ ...aulaRes.data, _cursoPai: res.data });
        return;
      }
      setSelectedItem(res.data);
    } catch (err) {
      toast.error(t('academia.empty.busca') || 'Erro ao carregar');
    } finally {
      setDetailLoading(false);
    }
  };

  // Abre uma aula a partir da página do curso (mantém referência ao pai)
  const openLesson = async (aula, cursoPai) => {
    if (detailLoading) return; // evita duplo clique durante o fetch
    try {
      setDetailLoading(true);
      const res = await axios.get(`${API}/academia/items/${aula.id}`);
      setSelectedItem({ ...res.data, _cursoPai: cursoPai });
    } catch (err) {
      toast.error(t('academia.empty.busca') || 'Erro ao carregar');
    } finally {
      setDetailLoading(false);
    }
  };

  // Toggle like (optimistic)
  const handleLike = async (item, e) => {
    if (e) e.stopPropagation();
    const prevLiked = item.user_liked;
    const prevCount = item.likes_count;

    // Optimistic update
    const newItems = items.map(i => i.id === item.id ? {
      ...i,
      user_liked: !prevLiked,
      likes_count: prevLiked ? prevCount - 1 : prevCount + 1,
    } : i);
    setItems(newItems);

    if (selectedItem && selectedItem.id === item.id) {
      setSelectedItem(prev => ({
        ...prev,
        user_liked: !prevLiked,
        likes_count: prevLiked ? prevCount - 1 : prevCount + 1,
      }));
    }

    try {
      await axios.post(`${API}/academia/items/${item.id}/like`);
    } catch (err) {
      // Revert on failure (lista E detalhe aberto — auditoria)
      setItems(items);
      setSelectedItem(prev => (prev && prev.id === item.id)
        ? { ...prev, user_liked: prevLiked, likes_count: prevCount }
        : prev);
    }
  };

  // Mark watched
  const handleWatched = async (itemId) => {
    try {
      await axios.post(`${API}/academia/items/${itemId}/watched`);
      // Update list
      setItems(prev => prev.map(i => i.id === itemId ? { ...i, user_watched: true } : i));
      if (selectedItem && selectedItem.id === itemId) {
        setSelectedItem(prev => ({ ...prev, user_watched: true }));
      }
      toast.success(t('academia.labels.assistido'));
      // Atualiza o progresso (hero "continue de onde parou" + barras dos cards)
      loadProgresso();
    } catch (err) {
      console.error('Error marking watched:', err);
    }
  };

  // ===== ADMIN CRUD =====

  const openAddModal = async () => {
    setEditingItem(null);
    const tipo = tipoMapping[activeTab] || 'treinamento';
    setFormData({ ...INITIAL_FORM, tipo });
    setTagInput('');
    setShowMoreDetails(false);
    setUploadPreview(null);
    setResourceThumbPreview(null);
    loadCourses();
    // Auto-calculate next ordem
    try {
      const res = await axios.get(`${API}/academia/next-ordem?tipo=${tipo}`);
      setFormData(prev => ({ ...prev, ordem: res.data.next_ordem || 0 }));
    } catch { /* keep default 0 */ }
    setShowAdminModal(true);
  };

  const openEditModal = async (item, e) => {
    if (e) e.stopPropagation();
    // Buscar detalhe completo (inclui conteudo_rich que não vem na listagem)
    let fullItem = item;
    try {
      const res = await axios.get(`${API}/academia/items/${item.id}`);
      fullItem = res.data;
    } catch (err) {
      console.warn('Falha ao buscar detalhe, usando dados da listagem:', err);
    }
    setEditingItem(fullItem);
    setFormData({
      ...INITIAL_FORM,
      ...fullItem,
      tags: fullItem.tags || [],
    });
    setTagInput('');
    setShowMoreDetails(false);
    // Restore upload preview from existing data
    if (fullItem.tipo === 'curso' && fullItem.thumbnail_url) {
      setUploadPreview({ url: fullItem.thumbnail_url, type: 'image', name: 'Capa existente' });
    } else if (fullItem.tipo === 'recurso' && fullItem.download_url && (fullItem.resource_type || 'file') !== 'link') {
      setUploadPreview({ url: fullItem.download_url, type: 'file', name: fullItem.tipo_arquivo ? `arquivo.${fullItem.tipo_arquivo}` : 'Arquivo existente' });
    } else {
      setUploadPreview(null);
    }
    // Restore resource thumbnail if exists
    if (fullItem.tipo === 'recurso' && fullItem.thumbnail_url) {
      setResourceThumbPreview({ url: fullItem.thumbnail_url, name: 'Thumbnail existente' });
    } else {
      setResourceThumbPreview(null);
    }
    loadCourses();
    setShowAdminModal(true);
  };

  const handleSave = async () => {
    if (!formData.titulo.trim()) {
      toast.error(t('academia.admin.titulo') + ' is required');
      return;
    }
    // Validação de recurso com link externo
    if (formData.tipo === 'recurso' && formData.resource_type === 'link') {
      if (!formData.download_url?.trim()) {
        toast.error('URL do recurso é obrigatória para links externos');
        return;
      }
      if (!formData.download_url.startsWith('https://')) {
        toast.error('A URL deve começar com https://');
        return;
      }
    }
    setSaving(true);
    try {
      if (editingItem) {
        await axios.put(`${API}/academia/items/${editingItem.id}`, formData);
        toast.success(t('academia.admin.item_atualizado'));
      } else {
        await axios.post(`${API}/academia/items`, formData);
        toast.success(t('academia.admin.item_criado'));
      }
      setShowAdminModal(false);
      setResourceThumbPreview(null);
      loadItems();
      // Catálogo das fileiras + progresso ficam frescos (auditoria)
      loadInicio();
      loadProgresso();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error saving');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(t('academia.admin.confirmar_deletar'))) return;
    try {
      await axios.delete(`${API}/academia/items/${item.id}`);
      toast.success(t('academia.admin.item_deletado'));
      if (selectedItem && selectedItem.id === item.id) setSelectedItem(null);
      loadItems();
      loadInicio();
      loadProgresso();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error deleting');
    }
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }));
    }
    setTagInput('');
  };

  const removeTag = (tag) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  // Upload file to R2
  const handleUpload = async (file, purpose) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/academia/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { url, ext, content_type } = res.data;

      if (purpose === 'cover') {
        setFormData(prev => ({ ...prev, thumbnail_url: url }));
        setUploadPreview({ url, type: 'image', name: file.name });
      } else if (purpose === 'resource-thumb') {
        setFormData(prev => ({ ...prev, thumbnail_url: url }));
        setResourceThumbPreview({ url, name: file.name });
      } else {
        setFormData(prev => ({ ...prev, download_url: url, tipo_arquivo: ext }));
        setUploadPreview({ url, type: 'file', name: file.name, ext });
      }
      toast.success('Upload concluído!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro no upload');
    } finally {
      setUploading(false);
    }
  };

  // Fetch next ordem when tipo changes in add mode
  useEffect(() => {
    if (!editingItem && showAdminModal && formData.tipo) {
      axios.get(`${API}/academia/next-ordem?tipo=${formData.tipo}`)
        .then(res => setFormData(prev => ({ ...prev, ordem: res.data.next_ordem || 0 })))
        .catch(() => {});
    }
  }, [formData.tipo, editingItem, showAdminModal]);

  // Auto-thumbnail from YouTube URL
  useEffect(() => {
    if (formData.video_url && !formData.thumbnail_url) {
      const thumb = getThumbnail(formData.video_url);
      if (thumb) setFormData(prev => ({ ...prev, thumbnail_url: thumb }));
    }
  }, [formData.video_url]);

  // ===== TAB CONFIG =====
  const tabs = [
    { key: 'inicio', label: t('academia.tabs.inicio') || 'Início', icon: Home },
    { key: 'treinamento', label: t('academia.tabs.treinamento'), icon: Play },
    { key: 'cursos', label: t('academia.tabs.cursos'), icon: BookOpen },
    { key: 'recursos', label: t('academia.tabs.recursos'), icon: Download },
  ];

  // ===== RENDER =====
  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
      <Sidebar onLogout={onLogout} />
      <Header />
      <main className="dp-main-content" style={{
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
        marginTop: '60px',
        flex: 1,
        minWidth: 0,
        transition: 'margin-left 0.3s ease',
        animation: 'dp-pageFadeIn 0.35s ease',
      }}>

        {/* PAGE HEADER */}
        <PageHeader
          icon={<GraduationCap size={22} color="var(--dp-blue-500)" />}
          title={t('academia.title')}
          description={t('academia.subtitle')}
          badge={(() => {
            const n = inicioData.cursos.length + inicioData.treinamentos.length + inicioData.recursos.length;
            return n > 0 ? `${n} ${t('academia.home.badge_items') || 'conteúdos'}` : undefined;
          })()}
        >
          {isSuperAdmin && (
            <button
              className="dp-btn-cta dp-btn-cta--sm"
              onClick={openAddModal}
            >
              <Plus size={14} /> {t('academia.admin.adicionar')} <ArrowRight size={14} />
            </button>
          )}
        </PageHeader>

        {/* BARRA DISCRETA (padrão Netflix): pills de seção + busca global */}
        <div style={{
          display: 'flex', gap: '10px', marginBottom: '22px',
          alignItems: 'center', flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', flex: '1 1 auto' }}>
            {tabs.map(tab => {
              const Icon = tab.icon;
              const active = activeTab === tab.key && !debouncedSearch;
              return (
                <button
                  key={tab.key}
                  onClick={() => { setActiveTab(tab.key); setSelectedItem(null); setSearchTerm(''); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '7px 14px', borderRadius: '999px',
                    background: active ? 'rgba(13, 174, 255, 0.10)' : 'transparent',
                    color: active ? 'var(--dp-blue-500)' : 'var(--dp-text-secondary)',
                    border: active ? '1px solid rgba(13, 174, 255, 0.3)' : '1px solid var(--dp-border-subtle)',
                    cursor: 'pointer', fontSize: '12.5px', fontFamily: 'var(--dp-font)',
                    fontWeight: active ? 600 : 500, transition: 'all 0.2s', whiteSpace: 'nowrap',
                  }}
                >
                  <Icon size={14} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Busca global (cross-seção) */}
          <div style={{ flex: '0 1 280px', position: 'relative', minWidth: '180px' }}>
            <Search size={14} style={{
              position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
              color: 'var(--dp-text-tertiary)', pointerEvents: 'none',
            }} />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder={t('academia.search_placeholder')}
              style={{
                width: '100%', padding: '8px 12px 8px 34px',
                borderRadius: '999px', border: '1px solid var(--dp-border-subtle)',
                background: 'var(--dp-bg-input)', color: 'var(--dp-text-primary)',
                fontSize: '12.5px', fontFamily: 'var(--dp-font)', outline: 'none',
                transition: 'border-color 0.2s',
              }}
              onFocus={e => e.target.style.borderColor = 'var(--dp-blue-500)'}
              onBlur={e => e.target.style.borderColor = 'var(--dp-border-subtle)'}
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} style={{
                position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', color: 'var(--dp-text-tertiary)',
                padding: '4px', display: 'flex',
              }}>
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* CONTENT AREA */}
        {selectedItem ? (
          selectedItem.tipo === 'curso' ? (
            /* ===== PÁGINA DO CURSO (linguagem Netflix) ===== */
            <CourseView
              curso={selectedItem}
              materiais={inicioData.recursos.filter(r => r.categoria && r.categoria === selectedItem.categoria)}
              onBack={() => setSelectedItem(null)}
              onOpenLesson={(aula) => openLesson(aula, selectedItem)}
              onResume={() => {
                const aulas = selectedItem.aulas || [];
                const next = aulas.find(a => !a.user_watched) || aulas[0];
                if (next) openLesson(next, selectedItem);
              }}
              onOpenMaterial={(mat) => openDetail(mat)}
              onLike={(item) => handleLike(item)}
              isSuperAdmin={isSuperAdmin}
              onEdit={openEditModal}
              onDelete={handleDelete}
              t={t}
              isMobile={isMobile}
            />
          ) : (
          /* ===== PLAYER (treinamento / aula / recurso) ===== */
          <div style={{
            display: 'flex', gap: '24px',
            flexDirection: isMobile ? 'column' : 'row',
            animation: 'dp-fade-in 0.3s ease-out',
          }}>
            {/* LEFT PANEL */}
            <div style={{ flex: isMobile ? 'none' : '0 0 60%', minWidth: 0 }}>
              {/* Back button: aula volta pro CURSO; demais voltam à grade */}
              <button
                onClick={() => {
                  const pai = selectedItem._cursoPai;
                  if (pai) openDetail({ id: pai.id });
                  else setSelectedItem(null);
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '16px',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--dp-blue-500)', fontSize: '13px', fontFamily: 'var(--dp-font)',
                  fontWeight: 500, padding: 0,
                }}
              >
                <ArrowLeft size={16} /> {t('academia.labels.voltar')}
              </button>

              {/* Ações admin do item aberto (cobre mobile, onde o overlay
                  de hover do MediaCard não existe) */}
              {isSuperAdmin && (
                <span style={{ display: 'inline-flex', gap: '6px', marginLeft: '12px', verticalAlign: 'middle' }}>
                  <button onClick={(e) => openEditModal(selectedItem, e)} style={{
                    background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)',
                    borderRadius: '8px', padding: '6px', cursor: 'pointer', display: 'inline-flex',
                  }}>
                    <Edit3 size={13} style={{ color: 'var(--dp-text-secondary)' }} />
                  </button>
                  <button onClick={(e) => handleDelete(selectedItem, e)} style={{
                    background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)',
                    borderRadius: '8px', padding: '6px', cursor: 'pointer', display: 'inline-flex',
                  }}>
                    <Trash2 size={13} style={{ color: '#F87171' }} />
                  </button>
                </span>
              )}

              {/* Video Player or Course cover or Resource icon */}
              {selectedItem.video_url ? (
                <div style={{
                  position: 'relative', paddingTop: '56.25%', background: '#000',
                  borderRadius: borderRadius.card, overflow: 'hidden', marginBottom: '16px',
                }}>
                  <iframe
                    src={`https://www.youtube.com/embed/${extractYoutubeId(selectedItem.video_url)}?autoplay=1`}
                    title={selectedItem.titulo}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                  />
                </div>
              ) : (selectedItem.tipo === 'curso' || selectedItem.tipo === 'treinamento') && selectedItem.thumbnail_url && !selectedItem.video_url ? (
                <div style={{ marginBottom: '16px' }}>
                  <img
                    src={selectedItem.thumbnail_url}
                    alt={selectedItem.titulo}
                    style={{
                      maxWidth: '400px', width: '100%', aspectRatio: '16/9',
                      objectFit: 'cover', borderRadius: borderRadius.card,
                      display: 'block',
                    }}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                </div>
              ) : selectedItem.tipo === 'recurso' ? (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  padding: '24px',
                  background: 'var(--dp-bg-card)',
                  borderRadius: borderRadius.card, border: '1px solid var(--dp-border-subtle)',
                  marginBottom: '16px',
                }}>
                  {selectedItem.thumbnail_url ? (
                    <img
                      src={selectedItem.thumbnail_url}
                      alt={selectedItem.titulo}
                      style={{
                        maxWidth: '100%', maxHeight: '400px',
                        objectFit: 'contain', display: 'block',
                        borderRadius: '12px',
                      }}
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    (() => {
                      const cfg = FILE_TYPE_CONFIG[selectedItem.tipo_arquivo] || FILE_TYPE_CONFIG.txt;
                      const FileIcon = cfg.Icon;
                      return <FileIcon size={64} color={cfg.color} />;
                    })()
                  )}
                  <div style={{ textAlign: 'center', marginTop: '16px' }}>
                    {selectedItem.tamanho_arquivo && (
                      <p style={{ color: 'var(--dp-text-tertiary)', fontSize: '13px', margin: '0 0 12px 0' }}>
                        {t('academia.labels.tamanho')}: {selectedItem.tamanho_arquivo}
                      </p>
                    )}
                    {selectedItem.download_url && (() => {
                      const isLink = selectedItem.resource_type === 'link';
                      const ext = (selectedItem.tipo_arquivo || '').toLowerCase();
                      const opensInTab = !isLink && OPEN_IN_TAB_EXTENSIONS.includes(ext);
                      const linkInfo = isLink ? getExternalLinkInfo(selectedItem.download_url) : null;
                      const filename = selectedItem.titulo
                        ? `${selectedItem.titulo.replace(/[<>:"|?*\\\/]/g, '').trim()}.${ext || 'file'}`
                        : `recurso.${ext || 'file'}`;

                      if (isLink) {
                        // Link externo: abrir em nova aba
                        return (
                          <a
                            href={selectedItem.download_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: '8px',
                              padding: '12px 24px', borderRadius: borderRadius.button,
                              background: 'linear-gradient(135deg, #0DAEFF, #3B82F6)',
                              color: 'white', textDecoration: 'none', fontSize: '14px',
                              fontWeight: 600, fontFamily: 'var(--dp-font)',
                            }}
                          >
                            <ExternalLink size={18} /> {linkInfo?.label || 'Abrir link'}
                          </a>
                        );
                      } else if (opensInTab) {
                        // PDF/TXT: abrir em nova aba
                        return (
                          <a
                            href={selectedItem.download_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: '8px',
                              padding: '12px 24px', borderRadius: borderRadius.button,
                              background: 'linear-gradient(135deg, #0DAEFF, #3B82F6)',
                              color: 'white', textDecoration: 'none', fontSize: '14px',
                              fontWeight: 600, fontFamily: 'var(--dp-font)',
                            }}
                          >
                            <FileText size={18} /> Abrir {ext.toUpperCase()}
                          </a>
                        );
                      } else {
                        // Outros (.zip, .docx, etc.): forçar download via JS
                        return (
                          <button
                            onClick={() => {
                              toast.promise(
                                forceDownloadFile(selectedItem.download_url, filename),
                                { loading: 'Baixando...', success: 'Download concluído!', error: 'Erro no download' }
                              );
                            }}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: '8px',
                              padding: '12px 24px', borderRadius: borderRadius.button,
                              background: 'linear-gradient(135deg, #0DAEFF, #3B82F6)',
                              color: 'white', border: 'none', cursor: 'pointer', fontSize: '14px',
                              fontWeight: 600, fontFamily: 'var(--dp-font)',
                            }}
                          >
                            <Download size={18} /> {t('academia.labels.baixar_agora')}
                          </button>
                        );
                      }
                    })()}
                  </div>
                </div>
              ) : null}

              {/* Like + Watched bottom row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '16px' }}>
                <button
                  onClick={() => handleLike(selectedItem)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: selectedItem.user_liked ? '#EF4444' : 'var(--dp-text-secondary)',
                    fontSize: '13px', fontFamily: 'var(--dp-font)',
                  }}
                >
                  <Heart size={18} fill={selectedItem.user_liked ? '#EF4444' : 'none'} />
                  {selectedItem.likes_count} {t('academia.labels.likes')}
                </button>
                <span style={{ color: 'var(--dp-text-tertiary)', fontSize: '13px' }}>
                  <Eye size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                  {selectedItem.views_count} {t('academia.labels.views')}
                </span>
              </div>
            </div>

            {/* RIGHT PANEL — Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2 style={{ ...typography.h2, color: 'var(--dp-text-primary)', marginBottom: '12px' }}>
                {selectedItem.titulo}
              </h2>

              {/* Tags */}
              {selectedItem.tags && selectedItem.tags.length > 0 && (
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
                  {selectedItem.tags.map(tag => (
                    <span key={tag} style={{
                      padding: '3px 10px', borderRadius: '12px', fontSize: '11px',
                      background: 'rgba(13, 174, 255, 0.08)', color: 'var(--dp-blue-500)',
                      border: '1px solid rgba(13, 174, 255, 0.15)',
                    }}>
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Nivel badge */}
              {selectedItem.nivel && (
                <span style={{
                  display: 'inline-block', padding: '3px 10px', borderRadius: '12px',
                  fontSize: '11px', fontWeight: 600, marginBottom: '12px',
                  background: NIVEL_COLORS[selectedItem.nivel] || 'var(--dp-text-tertiary)',
                  color: 'white',
                }}>
                  {t(`academia.niveis.${selectedItem.nivel}`)}
                </span>
              )}

              {/* Course progress */}
              {selectedItem.progresso && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', fontSize: '12px',
                    color: 'var(--dp-text-tertiary)', marginBottom: '6px',
                  }}>
                    <span>{t('academia.labels.progresso')}</span>
                    <span>{selectedItem.progresso.assistidas} {t('academia.labels.de')} {selectedItem.progresso.total} {t('academia.labels.aulas')}</span>
                  </div>
                  <div style={{
                    height: '6px', borderRadius: '3px', background: 'var(--dp-bg-input)',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      height: '100%', borderRadius: '3px',
                      background: 'linear-gradient(90deg, #0DAEFF, #3B82F6)',
                      width: `${selectedItem.progresso.percentual}%`,
                      transition: 'width 0.3s ease',
                    }} />
                  </div>
                </div>
              )}

              {/* Description */}
              {selectedItem.descricao && (
                <p style={{ color: 'var(--dp-text-secondary)', fontSize: '14px', lineHeight: 1.6, marginBottom: '16px', whiteSpace: 'pre-wrap' }}>
                  {selectedItem.descricao}
                </p>
              )}

              {/* Rich content */}
              {selectedItem.conteudo_rich && (
                <div
                  className="academia-rich-content"
                  style={{
                    color: 'var(--dp-text-secondary)', fontSize: '14px', lineHeight: 1.7,
                    borderTop: '1px solid var(--dp-border-subtle)', paddingTop: '16px',
                    whiteSpace: 'pre-wrap',
                  }}
                  dangerouslySetInnerHTML={{ __html: selectedItem.conteudo_rich }}
                />
              )}

              {/* Mark as watched */}
              {!selectedItem.user_watched && selectedItem.tipo !== 'curso' && (
                <button
                  onClick={() => handleWatched(selectedItem.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '10px 20px', borderRadius: borderRadius.button,
                    background: 'rgba(52, 211, 153, 0.1)', color: '#34D399',
                    border: '1px solid rgba(52, 211, 153, 0.2)', cursor: 'pointer',
                    fontSize: '13px', fontWeight: 500, fontFamily: 'var(--dp-font)',
                    marginTop: '20px', transition: 'all 0.2s',
                  }}
                >
                  <CheckCircle size={16} /> {t('academia.labels.marcar_assistido')}
                </button>
              )}
              {selectedItem.user_watched && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '10px 20px', borderRadius: borderRadius.button,
                  background: 'rgba(52, 211, 153, 0.08)', color: '#34D399',
                  fontSize: '13px', fontWeight: 500, marginTop: '20px',
                }}>
                  <CheckCircle size={16} /> {t('academia.labels.assistido')}
                </div>
              )}

              {/* Comments Section */}
              <ComentariosSection
                conteudoTipo={selectedItem.tipo}
                conteudoId={selectedItem.id}
              />
            </div>
          </div>
          )
        ) : debouncedSearch ? (
          loading ? (
            /* Skeleton dos resultados da busca */
            <div style={{
              display: 'grid', gap: '12px',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))',
            }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="dp-skeleton" style={{ aspectRatio: '16 / 9', borderRadius: '10px' }} />
              ))}
            </div>
          ) : items.length === 0 ? (
          /* ===== EMPTY STATE (busca sem resultado) ===== */
          <div style={{
            textAlign: 'center', padding: '60px 20px',
            background: 'var(--dp-bg-card)', borderRadius: '16px',
            border: '1px solid var(--dp-border-subtle)',
          }}>
            <Search size={48} style={{ color: 'var(--dp-text-tertiary)', margin: '0 auto 16px', opacity: 0.5 }} />
            <p style={{ color: 'var(--dp-text-secondary)', fontSize: '16px', fontWeight: 600, margin: '0 0 8px 0' }}>
              {t('academia.search.no_results') || 'Nenhum resultado para'} "<span style={{ color: 'var(--dp-blue-500)' }}>{debouncedSearch}</span>"
            </p>
            <p style={{ color: 'var(--dp-text-tertiary)', fontSize: '13px', margin: '0 0 16px 0' }}>
              {t('academia.search.try_term') || 'Tente buscar por outro termo'}
            </p>
            <button
              onClick={() => setSearchTerm('')}
              style={{
                padding: '8px 16px', background: 'transparent',
                border: '1px solid var(--dp-border-subtle)', borderRadius: '8px',
                color: 'var(--dp-text-secondary)', fontSize: '13px', fontWeight: 500,
                cursor: 'pointer', fontFamily: 'var(--dp-font)',
              }}
            >
              {t('academia.search.clear') || 'Limpar busca'}
            </button>
          </div>
        ) : (
          <>
            {/* Resultados da busca: grid de thumbnails (padrão Netflix) */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: '14px',
            }}>
              <h2 style={{
                margin: 0, fontSize: '16px', fontWeight: 700, letterSpacing: '-0.2px',
                color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)',
              }}>
                {t('academia.search.results_title') || 'Resultados'}
                <span style={{ marginLeft: '8px', fontSize: '12px', fontWeight: 500, color: 'var(--dp-text-tertiary)' }}>
                  {items.length} · "{debouncedSearch}"
                </span>
              </h2>
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  display: 'flex', alignItems: 'center', gap: '4px',
                  background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                  color: 'var(--dp-blue-500)', fontSize: '12px', fontWeight: 600,
                  fontFamily: 'var(--dp-font)',
                }}
              >
                <X size={13} /> {t('academia.search.clear') || 'Limpar busca'}
              </button>
            </div>
            <div style={{
              display: 'grid', gap: '12px',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))',
            }}>
              {items.map(item => (
                <MediaCard
                  key={item.id}
                  item={item}
                  width={0}
                  progress={progressoMap[item.id]}
                  isNew={isNovidade(item)}
                  onOpen={openDetail}
                  isSuperAdmin={isSuperAdmin}
                  onEdit={openEditModal}
                  onDelete={handleDelete}
                  t={t}
                  isMobile={false}
                />
              ))}
            </div>
          </>
          )
        ) : (
          /* ===== FILEIRAS NETFLIX (hero + rows, todas as seções) ===== */
          <AcademiaHome
            section={activeTab}
            cursos={inicioData.cursos}
            treinamentos={inicioData.treinamentos}
            recursos={inicioData.recursos}
            progresso={progressoGeral}
            loading={inicioLoading}
            onOpenItem={(item) => openDetail(item)}
            onResumeCourse={(item) => openDetail(item, { resume: true })}
            isSuperAdmin={isSuperAdmin}
            onEditItem={openEditModal}
            onDeleteItem={handleDelete}
            layoutSecoes={layoutSecoes}
            layoutSubs={layoutSubs}
            onReorderSections={handleReorderSections}
            onReorderSubs={handleReorderSubs}
            onReorderItems={handleReorderItems}
            categoryFilter={categoryFilter}
            onCategoryFilter={setCategoryFilter}
            t={t}
            isMobile={isMobile}
          />
        )}

        {/* ===== ADMIN MODAL ===== */}
        {showAdminModal && (
          <div
            className="dp-modal-overlay"
            // Fecha no mouseDOWN direto no overlay: selecionar texto dentro
            // do modal e soltar fora NÃO fecha (o click nativo dispara no
            // ancestral comum e fechava o modal no meio da seleção).
            onMouseDown={(e) => { if (e.target === e.currentTarget) setShowAdminModal(false); }}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
              zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center',
              animation: 'dp-fade-in 0.2s ease-out', padding: '20px',
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: 'var(--dp-bg-card)', borderRadius: borderRadius.card,
                border: '1px solid var(--dp-border-subtle)', maxWidth: '700px',
                width: '100%', maxHeight: '85vh', overflow: 'visible',
                animation: 'dp-slide-up 0.3s ease-out', padding: '0',
              }}
            >
            <div style={{ padding: '24px', maxHeight: '85vh', overflowY: 'auto', overflowX: 'hidden' }}>
              {/* Modal header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ ...typography.h3, color: 'var(--dp-text-primary)', margin: 0 }}>
                  {editingItem ? t('academia.admin.editar') : t('academia.admin.adicionar')}
                </h3>
                <button onClick={() => setShowAdminModal(false)} style={{
                  background: 'none', border: 'none', cursor: 'pointer', color: 'var(--dp-text-tertiary)',
                }}>
                  <X size={20} />
                </button>
              </div>

              {/* Form */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {/* Tipo */}
                {!editingItem && (
                  <div>
                    <label style={labelStyle}>{t('academia.admin.tipo')}</label>
                    <CustomDropdown
                      options={[
                        { value: 'treinamento', label: t('academia.tabs.treinamento') },
                        { value: 'curso', label: t('academia.tabs.cursos') },
                        { value: 'aula', label: 'Aula' },
                        { value: 'recurso', label: t('academia.tabs.recursos') },
                      ]}
                      value={formData.tipo}
                      onChange={val => {
                        setFormData(prev => ({ ...prev, tipo: val }));
                        setUploadPreview(null);
                      }}
                    />
                  </div>
                )}

                {/* Titulo */}
                <div>
                  <label style={labelStyle}>{t('academia.admin.titulo')} *</label>
                  <input
                    type="text"
                    value={formData.titulo}
                    onChange={e => setFormData(prev => ({ ...prev, titulo: e.target.value }))}
                    style={inputStyle}
                  />
                </div>

                {/* Descrição — campo único de texto do item (o antigo
                    "Conteúdo rico" foi unificado aqui; quebras de linha
                    são preservadas na exibição) */}
                <div>
                  <label style={labelStyle}>{t('academia.admin.descricao')}</label>
                  <textarea
                    value={formData.descricao || ''}
                    onChange={e => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
                    rows={3}
                    style={{ ...inputStyle, minHeight: '76px', resize: 'vertical' }}
                  />
                </div>

                {/* Video URL (treinamento + aula) */}
                {(formData.tipo === 'treinamento' || formData.tipo === 'aula') && (
                  <div>
                    <label style={labelStyle}>URL do YouTube *</label>
                    <input
                      type="text"
                      value={formData.video_url || ''}
                      onChange={e => setFormData(prev => ({ ...prev, video_url: e.target.value }))}
                      placeholder="https://www.youtube.com/watch?v=..."
                      style={inputStyle}
                    />
                  </div>
                )}

                {/* Upload Capa (curso) — drag & drop zone */}
                {formData.tipo === 'curso' && (
                  <div>
                    <label style={labelStyle}>Capa do Curso *</label>
                    <input
                      ref={coverInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      style={{ display: 'none' }}
                      onChange={e => e.target.files?.[0] && handleUpload(e.target.files[0], 'cover')}
                    />
                    {uploadPreview && uploadPreview.type === 'image' ? (
                      <div style={{
                        position: 'relative', borderRadius: '10px', overflow: 'hidden',
                        border: '1px solid var(--dp-border-subtle)',
                      }}>
                        <img
                          src={uploadPreview.url}
                          alt="Capa"
                          style={{ width: '100%', height: '160px', objectFit: 'cover', display: 'block' }}
                        />
                        <button
                          onClick={() => { setUploadPreview(null); setFormData(prev => ({ ...prev, thumbnail_url: '' })); }}
                          style={{
                            position: 'absolute', top: '8px', right: '8px',
                            width: '28px', height: '28px', borderRadius: '50%',
                            background: 'rgba(0,0,0,0.6)', border: 'none', color: 'white',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => !uploading && coverInputRef.current?.click()}
                        onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; }}
                        onDragLeave={e => { e.currentTarget.style.borderColor = 'var(--dp-border-medium)'; }}
                        onDrop={e => {
                          e.preventDefault();
                          e.currentTarget.style.borderColor = 'var(--dp-border-medium)';
                          const f = e.dataTransfer.files?.[0];
                          if (f) handleUpload(f, 'cover');
                        }}
                        style={{
                          border: '2px dashed var(--dp-border-medium)', borderRadius: '10px',
                          padding: '28px 16px', textAlign: 'center', cursor: uploading ? 'wait' : 'pointer',
                          transition: 'all 0.2s', background: 'rgba(255,255,255,0.02)',
                        }}
                      >
                        {uploading ? (
                          <Loader2 size={24} style={{ margin: '0 auto', color: 'var(--dp-blue-500)', animation: 'spin 1s linear infinite' }} />
                        ) : (
                          <Image size={24} style={{ margin: '0 auto 8px', color: 'var(--dp-text-tertiary)' }} />
                        )}
                        <p style={{ fontSize: '13px', color: 'var(--dp-text-secondary)', margin: '4px 0 0' }}>
                          {uploading ? 'Enviando...' : 'Arraste uma imagem ou clique para selecionar'}
                        </p>
                        <p style={{ fontSize: '11px', color: 'var(--dp-text-tertiary)', margin: '4px 0 0' }}>
                          JPG, PNG ou WebP (max 10MB)
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Upload Thumbnail (recurso) — drag & drop zone */}
                {formData.tipo === 'recurso' && (
                  <div>
                    <label style={labelStyle}>Thumbnail (1280×720 — 16:9)</label>
                    <input
                      ref={resourceThumbInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      style={{ display: 'none' }}
                      onChange={e => e.target.files?.[0] && handleUpload(e.target.files[0], 'resource-thumb')}
                    />
                    {resourceThumbPreview ? (
                      <div style={{
                        position: 'relative', borderRadius: '10px', overflow: 'hidden',
                        border: '1px solid var(--dp-border-subtle)', marginBottom: '4px',
                      }}>
                        <img
                          src={resourceThumbPreview.url}
                          alt="Thumbnail"
                          style={{ width: '100%', height: '160px', objectFit: 'cover', display: 'block' }}
                        />
                        <button
                          onClick={() => { setResourceThumbPreview(null); setFormData(prev => ({ ...prev, thumbnail_url: '' })); }}
                          style={{
                            position: 'absolute', top: '8px', right: '8px',
                            width: '28px', height: '28px', borderRadius: '50%',
                            background: 'rgba(0,0,0,0.6)', border: 'none', color: 'white',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => !uploading && resourceThumbInputRef.current?.click()}
                        onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; }}
                        onDragLeave={e => { e.currentTarget.style.borderColor = 'var(--dp-border-medium)'; }}
                        onDrop={e => {
                          e.preventDefault();
                          e.currentTarget.style.borderColor = 'var(--dp-border-medium)';
                          const f = e.dataTransfer.files?.[0];
                          if (f) handleUpload(f, 'resource-thumb');
                        }}
                        style={{
                          border: '2px dashed var(--dp-border-medium)', borderRadius: '10px',
                          padding: '28px 16px', textAlign: 'center', cursor: uploading ? 'wait' : 'pointer',
                          transition: 'all 0.2s', background: 'rgba(255,255,255,0.02)',
                        }}
                      >
                        {uploading ? (
                          <Loader2 size={24} style={{ margin: '0 auto', color: 'var(--dp-blue-500)', animation: 'spin 1s linear infinite' }} />
                        ) : (
                          <Image size={24} style={{ margin: '0 auto 8px', color: 'var(--dp-text-tertiary)' }} />
                        )}
                        <p style={{ fontSize: '13px', color: 'var(--dp-text-secondary)', margin: '4px 0 0' }}>
                          {uploading ? 'Enviando...' : 'Arraste uma imagem ou clique para selecionar'}
                        </p>
                        <p style={{ fontSize: '11px', color: 'var(--dp-text-tertiary)', margin: '4px 0 0' }}>
                          JPG, PNG ou WebP — 1280×720 recomendado (max 10MB)
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Tipo do recurso + Upload/Link (recurso) */}
                {formData.tipo === 'recurso' && (
                  <>
                    {/* Seletor: Arquivo ou Link externo */}
                    <div>
                      <label style={labelStyle}>Tipo do recurso</label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {[
                          { value: 'file', label: '📁 Arquivo', desc: 'Upload para o servidor' },
                          { value: 'link', label: '🔗 Link externo', desc: 'Google Drive, Dropbox, etc.' },
                        ].map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                resource_type: opt.value,
                                // Limpa o campo oposto ao trocar de tipo
                                ...(opt.value === 'link' ? { tipo_arquivo: '', tamanho_arquivo: '' } : {}),
                                ...(opt.value === 'file' ? {} : {}),
                                download_url: prev.resource_type !== opt.value ? '' : prev.download_url,
                              }));
                              if (opt.value === 'link') setUploadPreview(null);
                            }}
                            style={{
                              flex: 1, padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
                              border: `2px solid ${formData.resource_type === opt.value ? 'var(--dp-blue-500)' : 'var(--dp-border-subtle)'}`,
                              background: formData.resource_type === opt.value ? 'rgba(13, 174, 255, 0.08)' : 'transparent',
                              color: formData.resource_type === opt.value ? 'var(--dp-blue-500)' : 'var(--dp-text-secondary)',
                              fontSize: '13px', fontWeight: 600, fontFamily: 'var(--dp-font)',
                              transition: 'all 0.2s',
                            }}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Upload de arquivo (quando tipo = 'file') */}
                    {formData.resource_type !== 'link' && (
                      <div>
                        <label style={labelStyle}>Arquivo *</label>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf,.txt,.md,.zip,.docx,.xlsx,.pptx,.csv"
                          style={{ display: 'none' }}
                          onChange={e => e.target.files?.[0] && handleUpload(e.target.files[0], 'file')}
                        />
                        {uploadPreview && uploadPreview.type === 'file' ? (
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px',
                            borderRadius: '10px', border: '1px solid var(--dp-border-subtle)',
                            background: 'rgba(255,255,255,0.02)',
                          }}>
                            {(() => {
                              const ext = uploadPreview.ext || formData.tipo_arquivo || '';
                              const cfg = FILE_TYPE_CONFIG[ext] || { color: 'var(--dp-text-tertiary)', Icon: FileText };
                              const FileIcon = cfg.Icon;
                              return (
                                <div style={{
                                  width: '40px', height: '40px', borderRadius: '8px',
                                  background: `${cfg.color}15`, display: 'flex',
                                  alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                }}>
                                  <FileIcon size={20} style={{ color: cfg.color }} />
                                </div>
                              );
                            })()}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--dp-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {uploadPreview.name}
                              </div>
                              <div style={{ fontSize: '11px', color: 'var(--dp-text-tertiary)', marginTop: '2px' }}>
                                {(uploadPreview.ext || formData.tipo_arquivo || '').toUpperCase()}
                              </div>
                            </div>
                            <button
                              onClick={() => { setUploadPreview(null); setFormData(prev => ({ ...prev, download_url: '', tipo_arquivo: '' })); }}
                              style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                color: 'var(--dp-text-tertiary)', padding: '4px',
                              }}
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <div
                            onClick={() => !uploading && fileInputRef.current?.click()}
                            onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; }}
                            onDragLeave={e => { e.currentTarget.style.borderColor = 'var(--dp-border-medium)'; }}
                            onDrop={e => {
                              e.preventDefault();
                              e.currentTarget.style.borderColor = 'var(--dp-border-medium)';
                              const f = e.dataTransfer.files?.[0];
                              if (f) handleUpload(f, 'file');
                            }}
                            style={{
                              border: '2px dashed var(--dp-border-medium)', borderRadius: '10px',
                              padding: '28px 16px', textAlign: 'center', cursor: uploading ? 'wait' : 'pointer',
                              transition: 'all 0.2s', background: 'rgba(255,255,255,0.02)',
                            }}
                          >
                            {uploading ? (
                              <Loader2 size={24} style={{ margin: '0 auto', color: 'var(--dp-blue-500)', animation: 'spin 1s linear infinite' }} />
                            ) : (
                              <Upload size={24} style={{ margin: '0 auto 8px', color: 'var(--dp-text-tertiary)' }} />
                            )}
                            <p style={{ fontSize: '13px', color: 'var(--dp-text-secondary)', margin: '4px 0 0' }}>
                              {uploading ? 'Enviando...' : 'Arraste um arquivo ou clique para selecionar'}
                            </p>
                            <p style={{ fontSize: '11px', color: 'var(--dp-text-tertiary)', margin: '4px 0 0' }}>
                              PDF, TXT, Markdown, ZIP, DOCX, XLSX (max 10MB)
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Campo de URL externa (quando tipo = 'link') */}
                    {formData.resource_type === 'link' && (
                      <div>
                        <label style={labelStyle}>URL do recurso *</label>
                        <input
                          type="url"
                          value={formData.download_url || ''}
                          onChange={e => setFormData(prev => ({ ...prev, download_url: e.target.value }))}
                          placeholder="https://drive.google.com/file/d/..."
                          style={inputStyle}
                        />
                        <p style={{ fontSize: '11px', color: 'var(--dp-text-tertiary)', margin: '6px 0 0' }}>
                          Cole o link do arquivo (Google Drive, Dropbox, OneDrive, etc.)
                        </p>
                        {formData.download_url && !formData.download_url.startsWith('https://') && (
                          <p style={{ fontSize: '11px', color: '#EF4444', margin: '4px 0 0' }}>
                            ⚠️ O link deve começar com https://
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* Nivel (curso) */}
                {formData.tipo === 'curso' && (
                  <div>
                    <label style={labelStyle}>{t('academia.admin.nivel')}</label>
                    <CustomDropdown
                      options={[
                        { value: '', label: '-' },
                        { value: 'iniciante', label: t('academia.niveis.iniciante') },
                        { value: 'intermediario', label: t('academia.niveis.intermediario') },
                        { value: 'avancado', label: t('academia.niveis.avancado') },
                      ]}
                      value={formData.nivel || ''}
                      onChange={val => setFormData(prev => ({ ...prev, nivel: val }))}
                    />
                  </div>
                )}

                {/* Curso pai (aula) */}
                {formData.tipo === 'aula' && (
                  <div>
                    <label style={labelStyle}>{t('academia.admin.curso_pai')} *</label>
                    <CustomDropdown
                      options={[
                        { value: '', label: '-' },
                        ...courses.map(c => ({ value: c.id, label: c.titulo })),
                      ]}
                      value={formData.curso_pai_id || ''}
                      onChange={val => setFormData(prev => ({ ...prev, curso_pai_id: val }))}
                    />
                  </div>
                )}

                {/* Categoria + Subcategoria (not for aula) — a subcategoria é
                    opcional; quando preenchida, o vídeo aparece agrupado dentro
                    dela na categoria (ex.: App DarkPlanner › Como instalar). */}
                {formData.tipo !== 'aula' && (
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                      <label style={labelStyle}>{t('academia.admin.categoria')}</label>
                      <input
                        type="text"
                        value={formData.categoria || ''}
                        onChange={e => setFormData(prev => ({ ...prev, categoria: e.target.value }))}
                        placeholder="Geral"
                        style={inputStyle}
                        list="academia-categorias"
                      />
                    </div>
                    <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                      <label style={labelStyle}>{t('academia.admin.subcategoria') || 'Subcategoria (opcional)'}</label>
                      <input
                        type="text"
                        value={formData.subcategoria || ''}
                        onChange={e => setFormData(prev => ({ ...prev, subcategoria: e.target.value }))}
                        placeholder={t('academia.admin.subcategoria_placeholder') || 'Ex.: Como instalar'}
                        style={inputStyle}
                        list="academia-subcategorias"
                      />
                    </div>
                    {/* Sugestões (datalist) a partir do catálogo já cadastrado */}
                    <datalist id="academia-categorias">
                      {[...new Set([
                        ...inicioData.cursos, ...inicioData.treinamentos, ...inicioData.recursos,
                      ].map(i => i.categoria).filter(Boolean))].map(c => <option key={c} value={c} />)}
                    </datalist>
                    <datalist id="academia-subcategorias">
                      {[...new Set([
                        ...inicioData.cursos, ...inicioData.treinamentos, ...inicioData.recursos,
                      ].map(i => i.subcategoria).filter(Boolean))].map(s => <option key={s} value={s} />)}
                    </datalist>
                  </div>
                )}

                {/* ──── Collapsible "Mais detalhes" ──── */}
                <div>
                  <button
                    onClick={() => setShowMoreDetails(!showMoreDetails)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px', width: '100%',
                      padding: '10px 0', background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--dp-text-tertiary)', fontSize: '12px', fontWeight: 600,
                      fontFamily: 'var(--dp-font)', letterSpacing: '0.5px', textTransform: 'uppercase',
                    }}
                  >
                    <ChevronDown
                      size={14}
                      style={{ transition: 'transform 0.2s', transform: showMoreDetails ? 'rotate(180deg)' : 'rotate(0)' }}
                    />
                    Mais detalhes
                  </button>

                  {showMoreDetails && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', paddingTop: '4px' }}>
                      {/* Tags */}
                      <div>
                        <label style={labelStyle}>{t('academia.admin.tags')}</label>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
                          {formData.tags && formData.tags.map(tag => (
                            <span key={tag} style={{
                              padding: '3px 10px', borderRadius: '12px', fontSize: '11px',
                              background: 'rgba(13, 174, 255, 0.1)', color: 'var(--dp-blue-500)',
                              display: 'flex', alignItems: 'center', gap: '4px',
                            }}>
                              {tag}
                              <button onClick={() => removeTag(tag)} style={{
                                background: 'none', border: 'none', cursor: 'pointer', color: 'var(--dp-blue-500)',
                                padding: 0, fontSize: '14px', lineHeight: 1,
                              }}>
                                &times;
                              </button>
                            </span>
                          ))}
                        </div>
                        <input
                          type="text"
                          value={tagInput}
                          onChange={e => setTagInput(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                          placeholder={t('academia.admin.tags_placeholder')}
                          style={inputStyle}
                        />
                      </div>

                      {/* Ordem do card na seção (as setas do Início fazem o
                          mesmo; aqui é o ajuste fino numérico) */}
                      <div>
                        <label style={labelStyle}>{t('academia.admin.ordem')}</label>
                        <input
                          type="number"
                          value={formData.ordem ?? 0}
                          onChange={e => setFormData(prev => ({ ...prev, ordem: parseInt(e.target.value, 10) || 0 }))}
                          style={{ ...inputStyle, maxWidth: '140px' }}
                        />
                      </div>

                      {/* Campo "Conteúdo rico" removido (2026-06): descrição e
                          conteúdo detalhado eram a mesma coisa. conteudo_rich
                          legado é preservado no save e continua renderizando. */}
                    </div>
                  )}
                </div>

                {/* Save button */}
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '8px' }}>
                  <button
                    onClick={() => setShowAdminModal(false)}
                    style={{
                      padding: '10px 20px', borderRadius: borderRadius.button,
                      border: '1px solid var(--dp-border-subtle)', background: 'transparent',
                      color: 'var(--dp-text-secondary)', cursor: 'pointer', fontSize: '13px',
                      fontFamily: 'var(--dp-font)',
                    }}
                  >
                    {t('academia.admin.cancelar')}
                  </button>
                  <button
                    className="dp-btn-cta dp-btn-cta--sm"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? '...' : t('academia.admin.salvar')}
                    <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            </div>
            </div>
          </div>
        )}

        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes dp-fade-in {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes dp-slide-up {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .play-overlay { opacity: 0; transition: opacity 0.2s; }
          div:hover > .play-overlay { opacity: 1; }
          .academia-rich-content a { color: var(--dp-blue-500); text-decoration: underline; }
          .academia-rich-content h1, .academia-rich-content h2, .academia-rich-content h3 {
            color: var(--dp-text-primary); margin-top: 16px; margin-bottom: 8px;
          }
          .academia-rich-content ul, .academia-rich-content ol {
            padding-left: 20px; margin: 8px 0;
          }
          .academia-rich-content p { margin: 8px 0; }
        `}</style>
      </main>
    </div>
  );
};

// Shared styles
const labelStyle = {
  display: 'block', fontSize: '12px', fontWeight: 600,
  color: 'var(--dp-text-secondary)', marginBottom: '4px',
  fontFamily: 'var(--dp-font)',
};

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: '8px',
  border: '1px solid var(--dp-border-subtle)', background: 'var(--dp-bg-input)',
  color: 'var(--dp-text-primary)', fontSize: '13px', fontFamily: 'var(--dp-font)',
  outline: 'none', boxSizing: 'border-box',
};

export default AcademiaPage;
